import type { JSX } from 'react';
import { useBrand } from '../brand/context.js';
import { useI18n } from '../i18n/context.js';

/**
 * A generation failed. Says so in plain words, keeps the provider's message for support
 * (on hover, and in logs), and says how to reach a human: "contact us" with no address
 * left a stuck parent hunting for one.
 */
export function GenerationFailed({
  error,
  generationId,
  className = '',
}: {
  readonly error: string;
  /** The reference code support looks the failure up by; prefilled into the email subject. */
  readonly generationId?: string | null;
  readonly className?: string;
}): JSX.Element {
  const { t } = useI18n();
  const { support } = useBrand();
  const reference = generationId ? t.common.generationRef(generationId) : null;
  const href = `mailto:${support.email}${
    reference ? `?subject=${encodeURIComponent(reference)}` : ''
  }`;

  return (
    <div role="alert" className={`grid gap-1 text-sm ${className}`}>
      <p className="text-red-700" title={error}>
        {t.common.generationFailed}
      </p>
      {reference ? (
        <p className="font-mono text-xs break-all text-[var(--kitup-color-muted)]">{reference}</p>
      ) : null}
      <p className="text-[var(--kitup-color-muted)]">
        <a className="underline" href={href}>
          {support.email}
        </a>
        {support.replyWithinHours ? ` · ${t.common.replyWithin(support.replyWithinHours)}` : null}
      </p>
    </div>
  );
}
